// §4.7 — THE CoA MANAGER, /quality/coa (spec 07 §2.10, §2.11, §3.6; slice
// 07-E; D-217).
//
// TWO TABS, per the spec element list: *Documents* — every certificate
// revision with preview / public-link / issue / revoke+reissue on the row —
// and *Templates* — the branding definitions. The DRAFT dialog is §3.6's
// composer: template, lot, language, kind, the printed customer name (D-217
// pt 3 — module 09's customer FK does not exist, so the certificate takes the
// name as INK), and the response's GAP REPORT rendered as M07-S11's remedy
// list ("Run CP-… on …", each linking to the inspections queue) — a gapped
// draft is CREATED, not refused, and Issue is the step the guard blocks.
//
// D-118, BOTH HALVES: a principal without `quality.coa.issue` (sales — they
// draft for their customers, they do not attest) gets NO issue/revoke
// control, not a greyed one; the rows stay fully readable. A control dead for
// a FIXABLE reason says so (offline / load error, each its own sentence).
//
// The PREVIEW action is a plain link to the API's `preview.pdf` — a draft
// streams watermarked bytes, an issued revision 302s to its FROZEN file
// (§2.11: nothing re-renders an attested certificate), and the browser is the
// right client for both. TEMPLATE LOGO UPLOAD IS DEFERRED, stated rather than
// stubbed: the form ships name/kind/product binding/header/footer/signatory —
// the §2.10 columns the renderer prints today — and the upload control lands
// with the shared branding-upload affordance, not as a bespoke one here.

import { formatTimestampDate } from "../../../../lib/datetime";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  LOCALE_META,
  SERVER_TEXT_LOCALES,
  type ServerTextLocale,
} from "@assessly/contracts/locales";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Link } from "@tanstack/react-router";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { RowMenu } from "../../../../components/row-menu";
import { Select } from "../../../../components/select";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { UnauthorizedError, problemTitle } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { isQualityReadRefused, useQualityPermissions } from "../hooks/permissions";
import {
  useCoaDocuments,
  useCoaTemplates,
  useDraftCoa,
  useIssueCoa,
  useRevokeCoa,
  useUpsertCoaTemplate,
  useQualityFilterProducts,
  type QualityCoaDocument,
  type QualityCoaGap,
  type QualityCoaTemplate,
} from "../hooks/quality-queries";

type Tab = "documents" | "templates";
type CoaStatus = "draft" | "issued" | "revoked";

const STATUS_VARIANT: Record<CoaStatus, "neutral" | "ok" | "warn"> = {
  draft: "neutral",
  issued: "ok",
  revoked: "warn",
};

export function CoaManagerScreen() {
  const { t, i18n } = useTranslation();
  const perms = useQualityPermissions();

  const refused = isQualityReadRefused(perms, "quality.coa.draft");
  const canIssue = perms.has("quality.coa.issue");
  const canManageTemplates = perms.has("quality.coa_template.manage");

  const [tab, setTab] = useState<Tab>("documents");
  const [statusFilter, setStatusFilter] = useState<"" | CoaStatus>("");

  const documents = useCoaDocuments(statusFilter === "" ? {} : { status: statusFilter }, !refused);
  const templates = useCoaTemplates(!refused);
  const issueMutation = useIssueCoa();

  // §3.6's composer + its returned gap report; the issue guard's 422 gaps
  // land in the same panel, so both roads to "what still blocks this
  // certificate" read one list.
  const [draftOpen, setDraftOpen] = useState(false);
  const [gaps, setGaps] = useState<QualityCoaGap[]>([]);
  const [gapsFor, setGapsFor] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<QualityCoaDocument | undefined>(undefined);
  const [editingTemplate, setEditingTemplate] = useState<QualityCoaTemplate | null | undefined>(
    undefined,
  );
  const [banner, setBanner] = useState<string | null>(null);

  async function copyLink(url: string): Promise<void> {
    await navigator.clipboard.writeText(url);
    setBanner(t("quality.coa.linkCopied"));
  }

  async function issue(row: QualityCoaDocument): Promise<void> {
    setBanner(null);
    try {
      await issueMutation.mutateAsync(row.id);
      setGaps([]);
      setGapsFor(null);
      setBanner(t("quality.coa.issued", { coaNo: row.coa_no }));
    } catch (err) {
      // The thrown shape is lib/request's RequestError: `code` on the error
      // itself, the RFC 9457 body at `.problem`, and `gaps` a TOP-LEVEL
      // extension member of that body (never an `extensions` wrapper — RFC
      // 9457 §3.2). The first cut read `err.body.extensions.gaps`, both
      // levels dead, so a real QUALITY_COA_GAPS refusal rendered the generic
      // banner with no remedy rows — the M07-S10/S11 certification wall.
      const { code, problem } = err as { code?: unknown; problem?: unknown };
      if (code === "QUALITY_COA_GAPS" && problem !== null && typeof problem === "object") {
        const gapsMember = (problem as { gaps?: unknown }).gaps;
        setGaps(Array.isArray(gapsMember) ? (gapsMember as QualityCoaGap[]) : []);
        setGapsFor(row.coa_no);
        return;
      }
      setBanner(problemTitle(err) ?? t("quality.coa.issueError"));
    }
  }

  const documentColumns: DataTableColumn<QualityCoaDocument>[] = [
    { key: "coa_no", header: t("quality.coa.colNo"), id: true },
    { key: "revision", header: t("quality.coa.colRevision"), render: (r) => String(r.revision) },
    {
      key: "kind",
      header: t("quality.coa.colKind"),
      render: (r) => t(`quality.coa.kind.${r.kind}`),
    },
    {
      key: "customer",
      header: t("quality.coa.colCustomer"),
      render: (r) => r.customer_name ?? "—",
    },
    { key: "language", header: t("quality.coa.colLanguage"), render: (r) => r.language },
    {
      key: "status",
      header: t("quality.coa.colStatus"),
      render: (r) => (
        <StatusChip
          variant={STATUS_VARIANT[r.status]}
          data-testid={`coa-status-${r.coa_no}-${String(r.revision)}`}
        >
          {t(`quality.coa.status.${r.status}`)}
        </StatusChip>
      ),
    },
    {
      key: "issued_at",
      header: t("quality.coa.colIssuedAt"),
      render: (r) => (r.issued_at === null ? "—" : formatTimestampDate(r.issued_at, i18n.language)),
    },
    {
      key: "actions",
      header: t("quality.coa.colActions"),
      actions: true,
      render: (r) => {
        // D-448 ruling 2: one primary action inline, the rest behind the row's
        // "More" button. A draft's primary is Issue for the role that may
        // issue it; otherwise the certificate itself (its PDF, or its preview
        // while a draft) is what the reader came for. A row left with a single
        // secondary keeps it inline, so a reader who may only copy a link is
        // not sent through a menu for one button.
        const preview =
          r.status !== "revoked" ? (
            <a
              key="preview"
              className="ds-link"
              href={`/v1/quality/coa-documents/${r.id}/preview.pdf`}
              target="_blank"
              rel="noreferrer"
              data-testid={`coa-preview-${r.coa_no}-${String(r.revision)}`}
            >
              {r.status === "issued" ? t("quality.coa.pdf") : t("quality.coa.preview")}
            </a>
          ) : null;
        const copy =
          r.public_url !== null ? (
            <Button
              key="copy"
              size="sm"
              variant="secondary"
              data-testid={`coa-copy-link-${r.coa_no}`}
              onClick={() => void copyLink(r.public_url ?? "")}
            >
              {t("quality.coa.copyLink")}
            </Button>
          ) : null;
        const issueButton =
          canIssue && r.status === "draft" ? (
            <Button
              key="issue"
              size="sm"
              data-testid={`coa-issue-${r.coa_no}-${String(r.revision)}`}
              disabled={issueMutation.isPending}
              onClick={() => void issue(r)}
            >
              {t("quality.coa.issue")}
            </Button>
          ) : null;
        const revoke =
          canIssue && r.status !== "revoked" ? (
            <Button
              key="revoke"
              size="sm"
              variant="secondary"
              data-testid={`coa-revoke-${r.coa_no}-${String(r.revision)}`}
              onClick={() => {
                setRevoking(r);
              }}
            >
              {t("quality.coa.revoke")}
            </Button>
          ) : null;
        const primary = issueButton ?? preview;
        const secondary = (issueButton !== null ? [preview, copy, revoke] : [copy, revoke]).filter(
          (node) => node !== null,
        );
        return (
          <span className="coa-actions ds-row-acts">
            {primary}
            {secondary.length > 1 ? (
              <RowMenu
                label={t("nav.moreActionsFor", { name: r.coa_no })}
                data-testid={`coa-row-menu-${r.coa_no}-${String(r.revision)}`}
              >
                {secondary}
              </RowMenu>
            ) : (
              secondary
            )}
          </span>
        );
      },
    },
  ];

  const templateColumns: DataTableColumn<QualityCoaTemplate>[] = [
    { key: "name", header: t("quality.coa.tplColName"), id: true },
    {
      key: "kind",
      header: t("quality.coa.colKind"),
      render: (r) => t(`quality.coa.kind.${r.kind}`),
    },
    {
      key: "binding",
      header: t("quality.coa.tplColBinding"),
      render: (r) => r.product_id ?? t("quality.coa.tplAllProducts"),
    },
    {
      key: "active",
      header: t("quality.coa.tplColActive"),
      render: (r) =>
        r.active ? (
          <StatusChip variant="ok">{t("quality.coa.tplActive")}</StatusChip>
        ) : (
          <StatusChip variant="neutral">{t("quality.coa.tplInactive")}</StatusChip>
        ),
    },
    {
      key: "actions",
      header: t("quality.coa.colActions"),
      actions: true,
      render: (r) =>
        canManageTemplates ? (
          <Button
            size="sm"
            data-testid={`coa-template-edit-${r.name}`}
            onClick={() => {
              setEditingTemplate(r);
            }}
          >
            {t("quality.coa.tplEdit")}
          </Button>
        ) : null,
    },
  ];

  const activeQuery = tab === "documents" ? documents : templates;

  return (
    <section className="quality-coa" data-testid="quality-coa">
      <PageHeader
        title={t("quality.coa.title")}
        actions={
          <>
            {tab === "documents" && !refused ? (
              <Button
                data-testid="coa-draft-open"
                onClick={() => {
                  setDraftOpen(true);
                }}
              >
                {t("quality.coa.draftNew")}
              </Button>
            ) : null}
            {tab === "templates" && canManageTemplates ? (
              <Button
                data-testid="coa-template-new"
                onClick={() => {
                  setEditingTemplate(null);
                }}
              >
                {t("quality.coa.tplNew")}
              </Button>
            ) : null}
          </>
        }
      />

      <div className="filter-bar" data-testid="coa-tabs">
        <Button
          size="sm"
          variant={tab === "documents" ? "primary" : "secondary"}
          data-testid="coa-tab-documents"
          onClick={() => {
            setTab("documents");
          }}
        >
          {t("quality.coa.tabDocuments")}
        </Button>
        <Button
          size="sm"
          variant={tab === "templates" ? "primary" : "secondary"}
          data-testid="coa-tab-templates"
          onClick={() => {
            setTab("templates");
          }}
        >
          {t("quality.coa.tabTemplates")}
        </Button>
        {tab === "documents" ? (
          <Field label={t("quality.coa.filterStatus")}>
            <Select
              value={statusFilter}
              data-testid="coa-filter-status"
              onChange={(e) => {
                setStatusFilter(e.target.value as "" | CoaStatus);
              }}
            >
              <option value="">{t("quality.coa.allStatuses")}</option>
              {(["draft", "issued", "revoked"] as const).map((value) => (
                <option key={value} value={value}>
                  {t(`quality.coa.status.${value}`)}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
      </div>

      {banner !== null ? (
        <p className="quality-banner" role="status" data-testid="coa-banner">
          {banner}
        </p>
      ) : null}

      {/* M07-S11's remedy list: one row per gap, the sentence the scenario
          quotes, each linking to the inspections queue. */}
      {gaps.length > 0 ? (
        <div className="quality-banner" role="alert" data-testid="coa-gaps">
          <p>{t("quality.coa.gapsTitle", { coaNo: gapsFor ?? "" })}</p>
          <ul>
            {gaps.map((gap) => (
              <li key={`${gap.stock_unit_id ?? ""}-${gap.check_item_id}`}>
                <Link to="/quality/inspections" data-testid="coa-gap-link">
                  {t(
                    gap.reason === "not_passed"
                      ? "quality.coa.gapNotPassed"
                      : "quality.coa.gapMissing",
                    { point: gap.control_point_code, unit: gap.unit_label ?? "" },
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {refused ? (
        <ForbiddenState title={t("quality.access.title")} body={t("quality.access.coa")} />
      ) : null}

      {!refused && activeQuery.fetchStatus === "paused" && (activeQuery.data ?? []).length === 0 ? (
        <OfflineState title={t("quality.offline.title")} body={t("quality.offline.body")} />
      ) : null}

      {!refused && activeQuery.isPending && activeQuery.fetchStatus !== "paused" ? (
        <TableSkeleton rows={5} cols={6} label={t("quality.common.loading")} />
      ) : null}

      {!refused &&
      activeQuery.error !== null &&
      !(activeQuery.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("quality.coa.error")}
          retryLabel={t("quality.common.retry")}
          requestIdLabel={t("quality.common.requestId")}
          requestId={requestIdOf(activeQuery.error)}
          onRetry={() => {
            void activeQuery.refetch();
          }}
        />
      ) : null}

      {!refused && tab === "documents" && documents.data !== undefined ? (
        documents.data.length === 0 ? (
          <EmptyState
            title={t("quality.coa.emptyTitle")}
            body={t("quality.coa.emptyBody")}
            data-testid="coa-empty"
          />
        ) : (
          <DataTable
            columns={documentColumns}
            rows={documents.data}
            rowKey={(r) => r.id}
            empty={null}
            caption={t("quality.coa.title")}
            data-testid="coa-documents-table"
          />
        )
      ) : null}

      {!refused && tab === "templates" && templates.data !== undefined ? (
        templates.data.length === 0 ? (
          <EmptyState
            title={t("quality.coa.tplEmptyTitle")}
            body={t("quality.coa.tplEmptyBody")}
            data-testid="coa-templates-empty"
          />
        ) : (
          <DataTable
            columns={templateColumns}
            rows={templates.data}
            rowKey={(r) => r.id}
            empty={null}
            caption={t("quality.coa.tabTemplates")}
            data-testid="coa-templates-table"
          />
        )
      ) : null}

      {draftOpen ? (
        <DraftDialog
          templates={(templates.data ?? []).filter((row) => row.active)}
          onClose={() => {
            setDraftOpen(false);
          }}
          onDrafted={(coaNo, draftGaps) => {
            setDraftOpen(false);
            setGaps(draftGaps);
            setGapsFor(draftGaps.length > 0 ? coaNo : null);
            setBanner(t("quality.coa.drafted", { coaNo }));
          }}
        />
      ) : null}

      {revoking !== undefined ? (
        <RevokeDialog
          document={revoking}
          onClose={() => {
            setRevoking(undefined);
          }}
          onRevoked={(coaNo) => {
            setRevoking(undefined);
            setBanner(t("quality.coa.revoked", { coaNo }));
          }}
        />
      ) : null}

      {editingTemplate !== undefined ? (
        <TemplateDialog
          template={editingTemplate}
          onClose={() => {
            setEditingTemplate(undefined);
          }}
          onSaved={() => {
            setEditingTemplate(undefined);
            setBanner(t("quality.coa.tplSaved"));
          }}
        />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// §3.6 steps 1–3 — the draft composer
// ---------------------------------------------------------------------------

function DraftDialog(props: {
  templates: QualityCoaTemplate[];
  onClose: () => void;
  onDrafted: (coaNo: string, gaps: QualityCoaGap[]) => void;
}) {
  const { t } = useTranslation();
  const draftMutation = useDraftCoa();
  const [templateId, setTemplateId] = useState(props.templates[0]?.id ?? "");
  const [lotId, setLotId] = useState("");
  const [language, setLanguage] = useState<ServerTextLocale>("ru");
  const [kind, setKind] = useState<"analysis" | "conformance">("analysis");
  const [customerName, setCustomerName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setError(null);
    try {
      const res = await draftMutation.mutateAsync({
        template_id: templateId,
        lot_id: lotId.trim(),
        language,
        kind,
        ...(customerName.trim() === "" ? {} : { customer_name: customerName.trim() }),
      });
      props.onDrafted(res.document.coa_no, res.gaps);
    } catch (err) {
      setError(problemTitle(err) ?? t("quality.coa.draftError"));
    }
  };

  return (
    <Modal
      label={t("quality.coa.draftNew")}
      heading={t("quality.coa.draftNew")}
      closeLabel={t("quality.common.cancel")}
      className="quality-modal"
      testId="coa-draft-dialog"
      onClose={props.onClose}
    >
      <Field label={t("quality.coa.fieldTemplate")}>
        <Select
          value={templateId}
          data-testid="coa-draft-template"
          onChange={(e) => {
            setTemplateId(e.target.value);
          }}
        >
          {props.templates.map((row) => (
            <option key={row.id} value={row.id}>
              {row.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t("quality.coa.fieldLot")}>
        <input
          className="ds-input"
          value={lotId}
          data-testid="coa-draft-lot"
          onChange={(e) => {
            setLotId(e.target.value);
          }}
        />
      </Field>
      <p className="quality-note">{t("quality.coa.fieldLotHint")}</p>
      <Field label={t("quality.coa.fieldCustomer")}>
        <input
          className="ds-input"
          value={customerName}
          data-testid="coa-draft-customer"
          onChange={(e) => {
            setCustomerName(e.target.value);
          }}
        />
      </Field>
      <p className="quality-note">{t("quality.coa.fieldCustomerHint")}</p>
      <Field label={t("quality.coa.fieldLanguage")}>
        <Select
          value={language}
          data-testid="coa-draft-language"
          onChange={(e) => {
            setLanguage(e.target.value as typeof language);
          }}
        >
          {/* The languages a CoA can be written in are the ones server text
              exists for (the registry's SERVER_TEXT_LOCALES), each named in
              itself rather than by its code. */}
          {SERVER_TEXT_LOCALES.map((value) => (
            <option key={value} value={value}>
              {LOCALE_META[value].endonym}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t("quality.coa.fieldKind")}>
        <Select
          value={kind}
          data-testid="coa-draft-kind"
          onChange={(e) => {
            setKind(e.target.value as typeof kind);
          }}
        >
          <option value="analysis">{t("quality.coa.kind.analysis")}</option>
          <option value="conformance">{t("quality.coa.kind.conformance")}</option>
        </Select>
      </Field>
      {error !== null ? (
        <p className="ds-field-error" role="alert" data-testid="coa-draft-error">
          {error}
        </p>
      ) : null}
      <div className="modal-actions">
        <Button variant="secondary" onClick={props.onClose}>
          {t("quality.common.cancel")}
        </Button>
        <Button
          data-testid="coa-draft-submit"
          disabled={templateId === "" || lotId.trim() === "" || draftMutation.isPending}
          onClick={() => void submit()}
        >
          {t("quality.coa.draftSubmit")}
        </Button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// §3.6 step 5 — revoke (+ reissue)
// ---------------------------------------------------------------------------

function RevokeDialog(props: {
  document: QualityCoaDocument;
  onClose: () => void;
  onRevoked: (coaNo: string) => void;
}) {
  const { t } = useTranslation();
  const revokeMutation = useRevokeCoa();
  const [reason, setReason] = useState("");
  const [reissue, setReissue] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setError(null);
    try {
      await revokeMutation.mutateAsync({ id: props.document.id, reason: reason.trim(), reissue });
      props.onRevoked(props.document.coa_no);
    } catch (err) {
      setError(problemTitle(err) ?? t("quality.coa.revokeError"));
    }
  };

  return (
    <Modal
      label={t("quality.coa.revokeTitle", { coaNo: props.document.coa_no })}
      heading={t("quality.coa.revokeTitle", { coaNo: props.document.coa_no })}
      closeLabel={t("quality.common.cancel")}
      className="quality-modal"
      testId="coa-revoke-dialog"
      onClose={props.onClose}
    >
      {/* §9.11's consequence, stated BEFORE the press: the customer link dies
          at the commit. */}
      <p className="quality-note">{t("quality.coa.revokeWarning")}</p>
      <Field label={t("quality.coa.fieldReason")}>
        <input
          className="ds-input"
          value={reason}
          data-testid="coa-revoke-reason"
          onChange={(e) => {
            setReason(e.target.value);
          }}
        />
      </Field>
      <label className="ds-check">
        <input
          type="checkbox"
          checked={reissue}
          data-testid="coa-revoke-reissue"
          onChange={(e) => {
            setReissue(e.target.checked);
          }}
        />
        {t("quality.coa.reissueNext")}
      </label>
      {error !== null ? (
        <p className="ds-field-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="modal-actions">
        <Button variant="secondary" onClick={props.onClose}>
          {t("quality.common.cancel")}
        </Button>
        <Button
          variant="danger"
          data-testid="coa-revoke-submit"
          disabled={reason.trim() === "" || revokeMutation.isPending}
          onClick={() => void submit()}
        >
          {t("quality.coa.revoke")}
        </Button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// §2.10 — the template form (create + edit through the one upsert door)
// ---------------------------------------------------------------------------

function TemplateDialog(props: {
  /** null = create; a row = edit. */
  template: QualityCoaTemplate | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const upsert = useUpsertCoaTemplate();
  const products = useQualityFilterProducts(true);
  const existing = props.template;
  const [name, setName] = useState(existing?.name ?? "");
  const [kind, setKind] = useState<"analysis" | "conformance">(existing?.kind ?? "analysis");
  const [productId, setProductId] = useState(existing?.product_id ?? "");
  const [headerEn, setHeaderEn] = useState(existing?.header_i18n["en"] ?? "");
  const [headerRu, setHeaderRu] = useState(existing?.header_i18n["ru"] ?? "");
  const [signatoryName, setSignatoryName] = useState(existing?.signatory_name ?? "");
  const [active, setActive] = useState(existing?.active ?? true);
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setError(null);
    try {
      await upsert.mutateAsync({
        ...(existing === null ? {} : { id: existing.id }),
        name: name.trim(),
        kind,
        product_id: productId === "" ? null : productId,
        header_i18n: {
          ...(headerEn.trim() === "" ? {} : { en: headerEn }),
          ...(headerRu.trim() === "" ? {} : { ru: headerRu }),
        },
        signatory_name: signatoryName.trim() === "" ? null : signatoryName.trim(),
        active,
      });
      props.onSaved();
    } catch (err) {
      setError(problemTitle(err) ?? t("quality.coa.tplError"));
    }
  };

  return (
    <Modal
      label={existing === null ? t("quality.coa.tplNew") : t("quality.coa.tplEdit")}
      heading={existing === null ? t("quality.coa.tplNew") : t("quality.coa.tplEdit")}
      closeLabel={t("quality.common.cancel")}
      className="quality-modal"
      testId="coa-template-dialog"
      onClose={props.onClose}
    >
      <Field label={t("quality.coa.tplFieldName")}>
        <input
          className="ds-input"
          value={name}
          data-testid="coa-template-name"
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
      </Field>
      <Field label={t("quality.coa.fieldKind")}>
        <Select
          value={kind}
          data-testid="coa-template-kind"
          onChange={(e) => {
            setKind(e.target.value as typeof kind);
          }}
        >
          <option value="analysis">{t("quality.coa.kind.analysis")}</option>
          <option value="conformance">{t("quality.coa.kind.conformance")}</option>
        </Select>
      </Field>
      <Field label={t("quality.coa.tplFieldProduct")}>
        <Select
          value={productId}
          data-testid="coa-template-product"
          onChange={(e) => {
            setProductId(e.target.value);
          }}
        >
          <option value="">{t("quality.coa.tplAllProducts")}</option>
          {(products.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.sku}
            </option>
          ))}
        </Select>
      </Field>
      <p className="quality-note">{t("quality.coa.tplFieldProductHint")}</p>
      <Field label={t("quality.coa.tplFieldHeaderEn")}>
        <textarea
          className="ds-input"
          rows={2}
          value={headerEn}
          data-testid="coa-template-header-en"
          onChange={(e) => {
            setHeaderEn(e.target.value);
          }}
        />
      </Field>
      <Field label={t("quality.coa.tplFieldHeaderRu")}>
        <textarea
          className="ds-input"
          rows={2}
          value={headerRu}
          data-testid="coa-template-header-ru"
          onChange={(e) => {
            setHeaderRu(e.target.value);
          }}
        />
      </Field>
      <Field label={t("quality.coa.tplFieldSignatory")}>
        <input
          className="ds-input"
          value={signatoryName}
          data-testid="coa-template-signatory"
          onChange={(e) => {
            setSignatoryName(e.target.value);
          }}
        />
      </Field>
      <label className="ds-check">
        <input
          type="checkbox"
          checked={active}
          data-testid="coa-template-active"
          onChange={(e) => {
            setActive(e.target.checked);
          }}
        />
        {t("quality.coa.tplActive")}
      </label>
      {error !== null ? (
        <p className="ds-field-error" role="alert" data-testid="coa-template-error">
          {error}
        </p>
      ) : null}
      <div className="modal-actions">
        <Button variant="secondary" onClick={props.onClose}>
          {t("quality.common.cancel")}
        </Button>
        <Button
          data-testid="coa-template-submit"
          disabled={name.trim() === "" || upsert.isPending}
          onClick={() => void submit()}
        >
          {t("quality.coa.tplSave")}
        </Button>
      </div>
    </Modal>
  );
}
